








































































































































































































































import { Component, Prop, Vue, Watch, } from "@/shared/vuepack";
import {
  AuthenticationProvider,
  authTypeMap,
  idpStatusChange,
  OktaFactorType,
  oktaFactorTypeMap,
  Permission,
  SsoState,
  UserAccountCredentialStatus,
  userAccountCredentialStatusMap
} from "@/shared/types";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import Button from "@/shared/components/ui/Button.vue";
import { getBankUserAccountNumber } from "@/shared/api/bankService";
import moment from "moment";
import { LoginHistory } from "@/employer/route-names";
import { useNotifierStore } from "@/shared/store/notifier";
import { useThemeStore } from "@/shared/store/theme";
import { useRolePermissionsStore } from "@/shared/store/rolePermissions";
import { useUserIdpInformationStore } from "@/employer/store/userIdpInformation";

const statusToDisableIdpEmailEdit = [
  UserAccountCredentialStatus.None,
  UserAccountCredentialStatus.Suspended,
  UserAccountCredentialStatus.Deprovisioned
];

@Component({
  components: {
    GenericLoadingSpinner,
    Button
  }
})
export default class AccountHolderCredentials extends Vue {
  authTypeMap = authTypeMap;
  idpStatusChange = idpStatusChange;
  oktaFactorTypeMap = oktaFactorTypeMap;
  userAccountCredentialStatusMap = userAccountCredentialStatusMap;
  LoginHistory = LoginHistory;

  userIdpInformationStore = useUserIdpInformationStore();
  rolePermissionsStore = useRolePermissionsStore();
  notifierStore = useNotifierStore();
  themeStore = useThemeStore();

  showConfirmResetModal = false;
  showConfirmModal = false;
  isSubmitting = false;
  list: OktaFactorType[] = [];
  modalTitle = "";
  modalAction = idpStatusChange.None;
  idpEmailUpdate = "";
  isIdpEmailEditMode = false;
  isUpdatingEmail = false;
  showAccountResetModal = false;
  accountResetAcknowledgement = false;
  accountNumber = "";
  resetPasswordDelay = false;

  @Prop({ required: true })
  username: string;

  @Prop({ required: true })
  partnerId: number;

  mounted () {
    this.userIdpInformationStore.getProfile({
      partnerId: this.partnerId,
      username: this.username
    });

    if (this.canManageFraud) {
      getBankUserAccountNumber(this.username, this.partnerId)
        .then(response => {
          this.accountNumber = response.data.accountNumber;
        })
        .catch(() => {/* noop */});
    }
  }

  @Watch("profile")
  onProfileChanged () {
    this.$emit("profile-loaded", this.profile);
  }

  get profile () {
    return this.userIdpInformationStore.userProfile;
  }

  get isOkta () {
    return this.profile.provider === AuthenticationProvider.Okta;
  }

  get ssoUserId () {
    return this.profile.ssoUserId === null || this.profile.ssoUserId === "" ? "(not set)" : this.profile.ssoUserId;
  }

  get lastLogin () {
    return this.profile.lastLogin ? `${moment.utc(this.profile.lastLogin).format("llll")} UTC` : "None";
  }

  get isFailed () {
    return this.userIdpInformationStore.isProfileFailed;
  }

  get isLoaded () {
    return this.userIdpInformationStore.isProfileLoaded && !this.isUpdatingEmail;
  }

  get isActive () {
    return this.profile && this.profile.status === UserAccountCredentialStatus.Active;
  }

  get isPasswordExpired () {
    return this.profile && this.profile.status === UserAccountCredentialStatus.PasswordExpired;
  }

  get disableResetPassword () {
    return this.isSubmitting || this.resetPasswordDelay || (!this.isActive && !this.isPasswordExpired);
  }

  get disableResetMfa () {
    return this.isSubmitting || !this.isActive || !this.profile || this.profile.mfaTypes.length === 0;
  }

  get disableSuspend () {
    return this.isSubmitting || this.profile.status !== UserAccountCredentialStatus.Active;
  }

  get disableUnsuspend () {
    return this.isSubmitting || this.profile.status !== UserAccountCredentialStatus.Suspended;
  }

  get disableDeactivate () {
    return this.isSubmitting || this.profile.status !== UserAccountCredentialStatus.Active;
  }

  get isDeactivated () {
    return this.profile && this.profile.status === UserAccountCredentialStatus.Deprovisioned;
  }

  get isSuspended () {
    return this.profile && this.profile.status === UserAccountCredentialStatus.Suspended;
  }

  get isLocked () {
    return this.profile && this.profile.status === UserAccountCredentialStatus.LockedOut;
  }

  get showButtons () {
    return this.profile && this.profile.provider === AuthenticationProvider.Okta && this.profile.hasIdpAccount;
  }

  get hasMultiMfa () {
    return this.profile.mfaTypes && this.profile.mfaTypes.length > 1;
  }

  get hasPendingEmailVerification () {
    return this.profile && this.profile.pendingEmail;
  }

  get showSsoState () {
    return this.profile && this.profile.ssoUserSyncEnabled;
  }

  get ssoState () {
    return this.profile.hasIdpAccount ? SsoState.Enabled : SsoState.Pending;
  }

  get showEnableIdpAccount () {
    return this.showSsoState && this.ssoState === SsoState.Pending;
  }

  get ssoKey () {
    return this.profile.ssoKey;
  }

  get isCredentialedText () {
    return this.profile.isCredentialed ? "Yes" : "No";
  }

  onDisplayConfirmModal (title: string, action: idpStatusChange) {
    this.modalTitle = title;
    this.modalAction = action;
    this.showConfirmModal = true;
  }

  onCancelConfirmModal () {
    this.showConfirmModal = false;
  }

  onModalConfirm () {
    this.showConfirmModal = true;

    if (!this.isSubmitting) {
      this.isSubmitting = true;

      if (this.modalAction === idpStatusChange.Suspend) {
        this.userIdpInformationStore.changeSuspendStatus({
            username: this.username,
            statusChange: this.modalAction,
            oktaFactorTypes: null,
            partnerId: this.partnerId
          })
          .then(() => {
            this.notifierStore.setSuccess("User account status has been changed.");
          })
          .finally(() => {
            this.isSubmitting = false;
            this.showConfirmModal = false;
          });
      } else if (this.modalAction === idpStatusChange.Deactivate || this.modalAction === idpStatusChange.Active) {
        this.userIdpInformationStore.changeSuspendStatus({
            username: this.username,
            statusChange: this.modalAction,
            oktaFactorTypes: null,
            partnerId: this.partnerId
          })
          .then(() => {
            this.notifierStore.setSuccess("User account activation has been changed.");
          })
          .finally(() => {
            this.isSubmitting = false;
            this.showConfirmModal = false;
          });
      } else {
        this.userIdpInformationStore.changeStatus({
            username: this.username,
            statusChange: this.modalAction,
            oktaFactorTypes: null,
            partnerId: this.partnerId
          })
          .then(() => {
            this.notifierStore.setSuccess("User account status has been changed.");
          })
          .finally(() => {
            this.isSubmitting = false;
            this.showConfirmModal = false;
          });
      }
    }
  }

  onStatusChange (status: idpStatusChange) {
    if (!this.isSubmitting) {
      this.isSubmitting = true;

      if (status === idpStatusChange.ResetPassword) {
        this.setResetPasswordDelay();
      }

      this.userIdpInformationStore.changeStatus({
          username: this.username,
          statusChange: status,
          oktaFactorTypes: null,
          partnerId: this.partnerId
        })
        .then(() => {
          this.notifierStore.setSuccess("User account status has been changed.");
        })
        .finally(() => this.isSubmitting = false);
    }
  }

  setResetPasswordDelay () {
    this.resetPasswordDelay = true;

    setTimeout(() => {
      this.resetPasswordDelay = false;
    }, 10000);
  }

  onSuspendStatusChange (status: idpStatusChange) {
    if (!this.isSubmitting) {
      this.isSubmitting = true;

      this.userIdpInformationStore.changeSuspendStatus({
          username: this.username,
          statusChange: status,
          oktaFactorTypes: null,
          partnerId: this.partnerId
        })
        .then(() => {
          this.notifierStore.setSuccess("User account status has been changed.");
        })
        .finally(() => this.isSubmitting = false);
    }
  }

  addRemoveItem (mfaType: OktaFactorType) {
    const index = this.list.indexOf(mfaType);
    if (index > -1) {
      this.list.splice(index, 1);
    } else {
      this.list.push(mfaType);
    }
  }

  onMfaReset () {
    if (this.list && !this.isSubmitting) {
      this.isSubmitting = true;

      this.userIdpInformationStore.changeStatus({
          username: this.username,
          statusChange: idpStatusChange.ResetMfa,
          oktaFactorTypes: this.list,
          partnerId: this.partnerId
        })
        .then(() => {
          this.notifierStore.setSuccess("User multi-factor authentication options have been reset.");
        })
        .finally(() => {
          this.list = [];
          this.showConfirmResetModal = true;
          this.isSubmitting = false;
        });
    }
  }

  cancelResetMfa () {
    this.list = [];
    this.showConfirmResetModal = false;
  }

  showConfirmResetMfa () {
    this.$validator.validateAll()
      .then(isValid => {
        if (!isValid) {
          return;
        }

        this.showConfirmResetModal = true;
      });
  }

  onClickIdpEmailEdit () {
    this.idpEmailUpdate = this.profile.email;
    this.isIdpEmailEditMode = !this.isIdpEmailEditMode;
  }

  onClickCancelIdpEmailEdit () {
    this.isIdpEmailEditMode = false;
    this.idpEmailUpdate = this.profile.email;
  }

  get canSendIdpEmailChangeMessage (): boolean {
    return this.idpEmailUpdate !== this.profile.email && !this.$validator.errors.has("email");
  }

  onClickSendEmailVerificationMessage () {
    this.$validator.validateAll().then(result => {
      if (!result) {
        return;
      }

      this.isUpdatingEmail = true;

      this.userIdpInformationStore.sendIdpEmailVerificationMessage({
          username: this.username,
          email: this.idpEmailUpdate,
          partnerId: this.partnerId
        })
        .finally(() => {
          this.isUpdatingEmail = false;
          this.isIdpEmailEditMode = false;
        });
    });
  }

  onShowAccountResetModal () {
    this.showAccountResetModal = true;
  }

  onHideAccountResetModel () {
    this.accountResetAcknowledgement = false;
  }

  cancelAccountReset () {
    this.onHideAccountResetModel();
    this.showAccountResetModal = false;
  }

  onConfirmAccountReset () {
    this.userIdpInformationStore.resetIdpAccount({ username: this.username, partnerId: this.partnerId });
    this.showAccountResetModal = false;
  }

  onEnableAccount () {
    this.userIdpInformationStore.enableIdpAccount({ username: this.username, partnerId: this.partnerId });
  }

  onSyncAccount () {
    this.userIdpInformationStore.syncIdpAccount({ username: this.username, partnerId: this.partnerId });
  }

  get canManageUserLogin () {
    return this.rolePermissionsStore.userHasPartnerPermission(Permission.PartnerManageUserLogin, this.partnerId);
  }

  get canManageFraud () {
    return this.rolePermissionsStore.userHasPartnerPermission(Permission.PartnerManageIdp, this.partnerId);
  }

  get canViewMfaEmailEditButton () {
    return this.profile.provider === AuthenticationProvider.Okta && !this.isIdpEmailEditMode;
  }

  get disableIdpEmailEdit () {
    return !this.canManageFraud || statusToDisableIdpEmailEdit.some(status => status === this.profile.status);
  }
}
